import React from 'react';
import { Link as MuiLink } from '@mui/material';
import { Link } from 'react-router-dom';
import { supportArticleRoute } from '@uplift/constants';
import {
  ArticleBulletList,
  ArticleExample,
  ArticleParagraph,
  ArticleSection,
  ArticleSubsection,
} from './article-shared';
import { SUPPORT_ARTICLE_SLUGS } from './slugs';

export const PsychiatryDocumentationGuideArticle: React.FC = () => (
  <>
    <ArticleSection title="Purpose of Documentation Evaluation">
      <ArticleParagraph>
        At BetterHelp Psychiatry, clinical documentation is a cornerstone of high-quality, ethical, and effective patient
        care. Accurate and timely charting supports patient safety, medication continuity, audit readiness, and
        compliance with clinical and regulatory standards.
      </ArticleParagraph>
      <ArticleParagraph>
        To ensure consistent quality, BetterHelp conducts periodic Chart Audits using a standardized set of criteria. This
        process is educational and supportive, designed to strengthen provider practice, ensure appropriate medical
        decision-making, and enhance patient outcomes.
      </ArticleParagraph>
      <ArticleParagraph>
        This document outlines how each category of documentation is evaluated and how providers can ensure compliance
        with standards.{' '}
        <strong>
          Providers who consistently meet chart audit criteria demonstrate a strong commitment to excellence in client
          care and professional practice.
        </strong>
      </ArticleParagraph>
      <ArticleParagraph>
        <strong>Quick Tip:</strong> Think of your notes as a clear <em>clinical story</em>, showing why the patient is
        seeking care, what you assessed, how you intervened, and how you are ensuring safety.
      </ArticleParagraph>
      <ArticleParagraph>
        <strong>Telehealth Sessions & Clinical Record Documentation —</strong> For a provider to be compensated,
        telehealth sessions <strong>must</strong> be held on the BetterHelp platform (
        <MuiLink href="https://www.joinuplift.co" target="_blank" rel="noopener noreferrer">
          www.joinuplift.co
        </MuiLink>
        ). Audio-only telephone sessions are <strong>not</strong> reimbursable. The BetterHelp EMR must be used to document
        Psychiatric Evaluation and Medication Management sessions and Treat for e-prescribing.
      </ArticleParagraph>
      <ArticleBulletList
        items={[
          'The BetterHelp and prescribing platform are integrated and track patient treatment over time.',
          'The BetterHelp documentation platform is designed to guide you in effective documentation via Initial, Follow up, and Communication note templates.',
          'Although certain fields are not mandatory, it is still the provider’s responsibility to maintain thorough documentation in all necessary fields.',
        ]}
      />
    </ArticleSection>

    <ArticleSection title="Initial Intake (Psychiatric Consultation)">
      <ArticleParagraph>
        The psychiatric evaluation serves as the foundation for treatment planning and ongoing care. It must demonstrate
        comprehensive assessment, sound clinical judgment, and timely completion.
      </ArticleParagraph>

      <ArticleSubsection title="Chief Complaint">
        <ArticleBulletList
          items={[
            'Capture the patient’s primary reason for visit; this should reflect immediate concerns (e.g., “worsening anxiety,” “depressed mood”).',
          ]}
        />
      </ArticleSubsection>

      <ArticleSubsection title="History of Present Illness (HPI)">
        <ArticleBulletList
          items={[
            'Provide a narrative summary of the patient’s psychiatric history and current presentation. This section highlights the patient’s subjective description of the problem.',
            'Areas of focus include onset, duration, current symptoms, severity, exacerbating/relieving factors, historical patterns, and relevant psychosocial context.',
            'Document confirmation of client’s identity and location (state) at the start of each session.',
            'Integrate relevant collateral information or records reviewed.',
          ]}
        />
        <ArticleExample title="Example HPI">
          Patients reported chronic history of MDD that has been stable for 3+ years. Over the past 6 months however, he
          has noticed gradual worsening of depressive symptoms, which he partially attributes to losing his job. In
          recent months, in addition to persistent sadness, he notes poor motivation, sleep disturbance, and intermittent
          passive death wishes but denies active suicidal intent or plan. He is able to experience joy when around his
          children, but the moments of relief are brief in duration. He also reports moderately elevated anxiety that he
          rates 5/10 in severity, but denies a history of panic symptoms. The anxiety is mostly tied to financial
          concerns given his unemployment. He was previously on prozac which he found effective, but discontinued the
          medication due to improvement. He denies any additional acute concerns and is hopeful that re-engaging in
          treatment will help him “feel like [himself] again”.
        </ArticleExample>
        <ArticleParagraph>
          <strong>Tip:</strong> Avoid short-hand, bullet-style symptom lists; instead, document a concise, cohesive
          narrative that reflects the patient concerns to the reader.
        </ArticleParagraph>
      </ArticleSubsection>

      <ArticleSubsection title="Psychiatric Review of Systems">
        <ArticleBulletList
          items={[
            'The BetterHelp platform guides you through screening for positive and negative findings across major symptom domains (mood, anxiety, panic, sleep, appetite, abuse, trauma, psychosis, suicidality, etc.).',
            'Expand with free text commentary when relevant to clarify course, context, or trajectory.',
          ]}
        />
        <ArticleExample title="Example ROS free text">
          Reports history of childhood physical abuse that he processed in therapy as a young adult. Denies that the
          experience is presently disruptive or distressful in adult life. Denies recent nightmares, flashbacks or
          Intrusive thoughts.
        </ArticleExample>
      </ArticleSubsection>

      <ArticleSubsection title="Psychiatric and Substance Use History">
        <ArticleBulletList
          items={[
            'Complete all prompted sections, which include Diagnosis, Substance use history, Psychiatric medications (include current and previous), self harm, suicide attempts.',
            'If no data is entered into the prompted fields, the final note will default to “Denies” within the section. Therefore it is important to accurately report this information.',
            'Include any additional relevant details that may not be prompted (i.e. family history, previous hospitalizations/PHP, addiction rehabilitation, and other relevant treatments including history of psychotherapy, TMS, ECT, etc).',
          ]}
        />
      </ArticleSubsection>

      <ArticleSubsection title="Social History">
        <ArticleBulletList
          items={[
            'BetterHelp platform will prompt you to include history of developmental delay, legal history, military affiliation, accessibility to guns, and presence of social support.',
            'Although not currently prompted, it is important to consider and document relevant information about upbringing, religious/cultural considerations, sexual orientation and/or activity, relationships, marital status, children, family structure, education, employment, financial and housing stability, and other social determinants of health. This can be documented within the free text section labeled “additional information”.',
          ]}
        />
      </ArticleSubsection>

      <ArticleSubsection title="Medical History, Allergies, and Current Medications">
        <ArticleBulletList
          items={[
            'Document significant medical history, surgical history, allergies and non psychiatric medications (including over-the-counter and supplements) via prompted sections. If additional clarifying information is needed, please use the “additional information” section.',
          ]}
        />
      </ArticleSubsection>

      <ArticleSubsection title="Mental Status Examination (MSE)">
        <ArticleBulletList
          items={[
            'Complete all required fields within EMR prompts.',
            'If there is an additional need for free text documentation to clarify mental status, please use the additional information box under the assessment section.',
          ]}
        />
      </ArticleSubsection>

      <ArticleSubsection title="Risk Assessment">
        <ArticleBulletList
          items={[
            'Assess and document level of risk for the prompted categories.',
            'If there are additional considerations that are not prompted, please document under “additional information”.',
            'The free text section would also be an appropriate location to document your completed safety plan with the patient, if indicated (recommended, especially in cases with moderate or higher level of risk).',
          ]}
        />
      </ArticleSubsection>

      <ArticleSubsection title="Physical Review of Systems">
        <ArticleBulletList
          items={[
            'Complete the physical review of symptoms by selecting relevant positive symptoms.',
            'If there are any notable symptoms not present as an option, you may document under the HPI section as a patient complaint.',
          ]}
        />
      </ArticleSubsection>

      <ArticleSubsection title="Assessment">
        <ArticleBulletList
          items={[
            'Document and score any objective screening tools used or reviewed (e.g., PHQ, GAD).',
            'Write a brief clinical assessment outlining the direction of treatment while highlighting historical context, current presentation, diagnostic rationale, and anticipated trajectory of treatment.',
            'Indicate whether or not the level of care is appropriate for BetterHelp’s telehealth platform after your evaluation.',
          ]}
        />
      </ArticleSubsection>

      <ArticleSubsection title="Diagnosis">
        <ArticleBulletList
          items={[
            'Add all relevant diagnoses to the encounter. The diagnosis should be clearly supported by documented information from the patient’s narrative (HPI), objective information (reviewed records, screening tools, mental status exam), your clinical assessment, and the associated plan that follows the diagnosis.',
          ]}
        />
      </ArticleSubsection>

      <ArticleSubsection title="Plan">
        <ArticleParagraph>This section should detail important medical decision-making elements:</ArticleParagraph>
        <ArticleBulletList
          items={[
            'Medication management: start/stop/change with rationale, dose, indication, and treatment specific patient education provided.',
            'Additional interventions: labs ordered/reviewed, referrals, coordination of care, documentation completed (e.g., FMLA).',
            'Therapy/Education: time spent, focus areas, goals, duration.',
            'Other notable information: important medical or treatment related information that needs to be carried forward to next note/evaluation.',
            'Follow-up: date and rationale for follow up. Reason if not scheduled.',
          ]}
        />
      </ArticleSubsection>
    </ArticleSection>

    <ArticleSection title="Progress Notes">
      <ArticleParagraph>
        Progress notes are the ongoing clinical record and are reviewed for clinical relevance, intervention quality,
        and progression.
      </ArticleParagraph>
      <ArticleBulletList
        items={[
          'Document confirmation of client’s identity and location (state) at the start of each session.',
          'Chief complaint: Capture the patient’s current concerns, reflecting interval change or persistence of symptoms.',
          'History of present illness: Provide a concise summary of interval events and patient narrative. Document current symptoms and functional impairments, severity, trend, and other relevant interview information.',
          'Psychiatric Review of Symptoms: Complete and update all relevant symptom domains. Include clarifying comments or notable changes as free text when necessary.',
          'Mental Status Exam: Include MSE observations that reflect the client’s current functioning.',
          'Risk Assessment and Management: Update the risk assessment each visit. Modify or reaffirm the safety plan when appropriate.',
          'Physical review of systems: Review and update physical health screening elements for accuracy.',
          'Assessment: Provide a succinct summary of the patient’s current status, progress, and treatment trajectory.',
          'Diagnosis: Add, remove, or update diagnoses as indicated.',
        ]}
      />
      <ArticleSubsection title="Plan">
        <ArticleBulletList
          items={[
            'Medication changes with rationale, indication, and education provided',
            'Labs ordered or reviewed, referrals, and care coordination efforts',
            'Therapy interventions, education, and self-management strategies',
            'Follow-up interval and rationale for timing',
            'Documentation within this section should reflect the complexity and level of medical decision-making consistent with the billing code for the encounter.',
          ]}
        />
      </ArticleSubsection>
      <ArticleSubsection title="Psychotherapy / Education / Crisis Add-On">
        <ArticleBulletList
          items={[
            'If 16+ minutes of psychotherapy, psychoeducation, or crisis intervention is performed (in addition to medical decision making), document the service specifics, including therapy focus, duration, and goals, to support the use of the add-on code (e.g., 90833).',
            'Additional free text information may be included under Plan or at the end of the note, if needed.',
          ]}
        />
      </ArticleSubsection>
      <ArticleSubsection title="Key Considerations for Follow-Up / Progress Notes">
        <ArticleBulletList
          items={[
            'While the follow-up template does not prompt for psychiatric history, social history, family history, medical history, allergies, or self-reported medications, these elements should still be reviewed and updated periodically or when clinically indicated.',
            'Periodic review of these areas supports safety, accurate medication reconciliation, and comprehensive continuity of care.',
            'Documentation must reflect updated treatments, response, rationale, and level of risk to ensure alignment with clinical standards and audit readiness.',
          ]}
        />
      </ArticleSubsection>
    </ArticleSection>

    <ArticleSection title="Documentation Quick Reference Guide">
      <ArticleSubsection title="Do’s">
        <ArticleBulletList
          items={[
            'Obtain and document client’s consent for telehealth at intake; document confirmation of client’s identity and location (state) at the start of each session.',
            'Document any objective screening measurements or decision making processes used during the evaluation.',
            'Address any functional impairments in your plan (Social, occupational, daily living, etc).',
            'Update clinical information as indicated (medication reconciliation, medical history, etc).',
            'Document differential diagnosis in your clinical assessment when indicated, in addition to reasoning.',
            'Write your plan in a clear, comprehensive way, communicating direction to the care team.',
            'Include medication indications when prescribing medications.',
            'Include rationale/reasoning for off label prescribing or polypharmacy.',
            'Be detailed in documenting interventions (labs, referrals, and nonpharmacology interventions).',
            'Make sure the level of documentation supports the code you are billing for the encounter.',
            'Document client’s refusal of medication management at this time when applicable.',
            'Keep notes concise and clinically relevant (only copy forward information relevant to ongoing visit/treatment).',
          ]}
        />
      </ArticleSubsection>
      <ArticleSubsection title="Don’ts">
        <ArticleBulletList
          items={[
            'Don’t leave out relevant information from the visit — thorough documentation protects you and encourages care coordination (If it isn’t documented, it didn’t happen!).',
            'Don’t copy-paste the same intervention or progress note language repeatedly; show client-specific changes over time.',
            'Don’t include contradictory information within the note (e.g., Endorses SI in HPI and documenting no SI in risk assessment or MSE).',
            'Don’t delay documentation beyond required timelines.',
            'Don’t discharge a client without a summary of progress, current status, and referrals.',
          ]}
        />
      </ArticleSubsection>
      <ArticleParagraph>
        <strong>Other resources:</strong>{' '}
        <MuiLink component={Link} to={supportArticleRoute.createLink({ slug: SUPPORT_ARTICLE_SLUGS.cptCodingGuide })}>
          CPT Coding Reference Guide
        </MuiLink>
      </ArticleParagraph>
    </ArticleSection>
  </>
);
